import React from 'react';

/**
 * Ilustração de fundo FIXA do Hero da RyanProductPage — composição médica
 * padrão, igual para todos os produtos. Não depende do tema do campo de
 * estudo (RyanFieldPattern segue existindo só pros cards/header de campo) —
 * arquivo independente por design, sem importar nada de lá. A cena da mesa
 * de estudos segue o mesmo espírito visual do tema "Tutoria" de
 * RyanFieldPattern.tsx (mesa em elipse = perspectiva, alunos como
 * cabeça+ombros), só redesenhada aqui do zero.
 */

const STROKE = {
  fill: 'none',
  stroke: '#fff',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

interface IconProps {
  x: number;
  y: number;
  scale?: number;
  opacity?: number;
}

function HeroStethoscope({ x, y, scale = 1, opacity = 0.15 }: IconProps) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="-16" cy="-40" r="4.5" />
      <circle cx="16" cy="-40" r="4.5" />
      <path d="M-16 -36 v10 q0 20 16 20 t16 -20 v-6" />
      <path d="M0 -6 v22 q0 22 -16 22 t-16 22" />
      <circle cx="-16" cy="60" r="13" />
      <circle cx="-16" cy="60" r="5" />
    </g>
  );
}

function HeroHospital({ x, y, scale = 1, opacity = 0.13 }: IconProps) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <rect x="-38" y="-16" width="76" height="58" />
      <path d="M0 -6 v20 M-10 4 h20" strokeWidth={3.2} />
      <line x1="-28" y1="16" x2="-20" y2="16" />
      <line x1="20" y1="16" x2="28" y2="16" />
      <line x1="-28" y1="28" x2="-20" y2="28" />
      <line x1="20" y1="28" x2="28" y2="28" />
      <rect x="-6" y="26" width="12" height="16" />
    </g>
  );
}

function HeroAmbulance({ x, y, scale = 1, opacity = 0.12 }: IconProps) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-64 24 V0 h64 v-8 h28 l16 18 v14" />
      <rect x="-64" y="0" width="64" height="24" />
      <path d="M0 0 h28 l16 18 h-44 Z" />
      <circle cx="-40" cy="30" r="8" />
      <circle cx="28" cy="30" r="8" />
      <line x1="-32" y1="30" x2="20" y2="30" />
      <path d="M-42 6 v10 M-47 11 h10" strokeWidth={2.4} />
    </g>
  );
}

function HeroSusSign({ x, y, scale = 1, opacity = 0.14 }: IconProps) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M0 14 C0 -2 20 -6 26 8 C32 20 20 32 0 46 C-20 32 -32 20 -26 8 C-20 -6 0 -2 0 14 Z" />
      <path d="M0 10 v18 M-9 19 h18" strokeWidth={3} />
    </g>
  );
}

function HeroVirus({ x, y, scale = 1, opacity = 0.13 }: IconProps) {
  const spikes = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="20" />
      <circle cx="0" cy="0" r="4" />
      {spikes.map(deg => {
        const rad = (deg * Math.PI) / 180;
        return (
          <line
            key={deg}
            x1={20 * Math.cos(rad)}
            y1={20 * Math.sin(rad)}
            x2={28 * Math.cos(rad)}
            y2={28 * Math.sin(rad)}
          />
        );
      })}
      {spikes.map(deg => {
        const rad = (deg * Math.PI) / 180;
        return (
          <circle
            key={`c${deg}`}
            cx={28 * Math.cos(rad)}
            cy={28 * Math.sin(rad)}
            r="2.4"
            fill="#fff"
            stroke="none"
          />
        );
      })}
    </g>
  );
}

function HeroEcgLine({ x, y, scale = 1, opacity = 0.14 }: IconProps) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <path d="M-70 0 H-52 Q-48 -8 -44 0 H-36 L-32 7 L-29 -21 L-25 29 L-21 0 H-12 Q-6 -8 0 0 H70" strokeWidth={2.6} />
    </g>
  );
}

function HeroVaccine({ x, y, scale = 1, opacity = 0.13 }: IconProps) {
  return (
    <g transform={`translate(${x} ${y}) rotate(28) scale(${scale})`} opacity={opacity}>
      <line x1="0" y1="-52" x2="0" y2="-38" strokeWidth={3} />
      <line x1="-7" y1="-45" x2="7" y2="-45" strokeWidth={3} />
      <rect x="-11" y="-38" width="22" height="26" rx="2" />
      <line x1="-6" y1="-34" x2="-6" y2="-16" strokeWidth={1.3} opacity={0.7} />
      <line x1="0" y1="-34" x2="0" y2="-16" strokeWidth={1.3} opacity={0.7} />
      <line x1="6" y1="-34" x2="6" y2="-16" strokeWidth={1.3} opacity={0.7} />
      <line x1="0" y1="-12" x2="0" y2="34" strokeWidth={2.2} />
      <circle cx="0" cy="36" r="1.8" fill="#fff" stroke="none" />
    </g>
  );
}

/* ── Mesa de estudos em perspectiva — elipse (não círculo) já sugere o
   ângulo de visão, alunos como cabeça+ombros ao redor, maiores/mais opacos
   na frente e menores/mais esmaecidos ao fundo pra reforçar profundidade. ── */
function HeroTable({ x, y, rx = 145, ry = 30, opacity = 0.15 }: { x: number; y: number; rx?: number; ry?: number; opacity?: number }) {
  return <ellipse cx={x} cy={y} rx={rx} ry={ry} opacity={opacity} />;
}

function HeroPerson({ x, y, scale = 1, opacity = 0.16 }: IconProps) {
  return (
    <g transform={`translate(${x} ${y}) scale(${scale})`} opacity={opacity}>
      <circle cx="0" cy="0" r="9" />
      <path d="M-18 29q18-20 36 0" />
    </g>
  );
}

/**
 * A versão desktop usa uma viewBox bem "paisagem" (1200x460). No hero do
 * celular o container fica estreito e ALTO (oposto disso) — com
 * preserveAspectRatio="slice", isso força o SVG a ampliar pela altura pra
 * cobrir o container, empurrando a largura muito além do viewport. Sobrava
 * só o que estava perto do centro horizontal (o ECG, x=640) e gigante; o
 * resto (mesa, hospital, estetoscópio, vírus, ambulância) ficava cortado
 * pra fora. Por isso essa versão mobile usa uma viewBox própria, já no
 * formato retrato — não é um recorte da versão desktop, é uma composição
 * nova pensada pro formato certo, com vários desenhos espalhados na vertical
 * em vez de um só ampliado.
 */
const RyanProductHeroPatternMobile: React.FC = () => (
  <svg
    className="rpp-hero-pattern-svg rpp-hero-pattern-svg--mobile"
    viewBox="0 0 380 560"
    preserveAspectRatio="xMidYMid slice"
  >
    <g {...STROKE}>
      <HeroEcgLine x={190} y={90} scale={0.62} opacity={0.16} />
      <HeroStethoscope x={78} y={230} scale={0.78} opacity={0.14} />
      <HeroSusSign x={300} y={210} scale={0.72} opacity={0.13} />
      <HeroVirus x={90} y={400} scale={0.75} opacity={0.12} />
      <HeroVaccine x={295} y={410} scale={0.8} opacity={0.3} />
      <HeroHospital x={190} y={500} scale={0.82} opacity={0.12} />

      <circle cx="40" cy="130" r="2.6" opacity="0.11" />
      <circle cx="345" cy="330" r="3" opacity="0.1" />
      <circle cx="200" cy="480" r="2.2" opacity="0.1" />
    </g>
  </svg>
);

const RyanProductHeroPattern: React.FC = () => (
  <>
    <svg
      className="rpp-hero-pattern-svg rpp-hero-pattern-svg--desktop"
      viewBox="0 0 1200 460"
      preserveAspectRatio="xMidYMid slice"
    >
      <g {...STROKE}>
        {/* mesa de estudos com alunos, terço esquerdo — um em cada "canto" da
            elipse (2 de cada lado, mais um na cabeceira), bem colados na borda
            da mesa, não soltos flutuando acima dela. Opacidade bem mais alta
            que os outros ícones: essa é a cena principal do hero. */}
        <HeroTable x={195} y={365} rx={165} ry={36} opacity={0.24} />
        {/* fundo/"outro lado" — corpo tem que ficar inteiro acima da borda de
            trás da elipse (nunca cruzando pra dentro dela, senão parece
            sentado em cima da mesa em vez de atrás dela) */}
        <HeroPerson x={195} y={274} scale={1.55} opacity={0.28} />
        <HeroPerson x={78} y={289} scale={1.4} opacity={0.26} />
        <HeroPerson x={312} y={289} scale={1.4} opacity={0.26} />
        {/* frente — corpo desce um pouco abaixo da borda da frente, igual
            sentado numa cadeira em frente à mesa. Mais um pouco de aproximação
            (eram 108/282) pra ficarem ainda menos espalhados. */}
        <HeroPerson x={128} y={378} scale={1.35} opacity={0.24} />
        <HeroPerson x={262} y={378} scale={1.35} opacity={0.24} />

        <HeroHospital x={110} y={95} scale={1.35} opacity={0.12} />
        <HeroSusSign x={270} y={60} scale={1.05} opacity={0.13} />
        <HeroStethoscope x={1085} y={95} scale={1.25} opacity={0.14} />
        <HeroEcgLine x={640} y={415} scale={1.3} opacity={0.15} />
        <HeroVirus x={870} y={90} scale={1.1} opacity={0.12} />
        <HeroAmbulance x={1050} y={375} scale={1.05} opacity={0.11} />
        {/* vacina — mesmo nível vertical de antes, só deslocada mais pra
            esquerda. Opacidade um pouco mais alta pra compensar: aqui ela fica
            mais perto do centro mascarado que a posição anterior. */}
        <HeroVaccine x={930} y={250} scale={1.1} opacity={0.35} />

        <circle cx="60" cy="150" r="3" opacity="0.11" />
        <circle cx="1150" cy="230" r="3.5" opacity="0.1" />
        <circle cx="450" cy="60" r="2.4" opacity="0.1" />
      </g>
    </svg>

    <RyanProductHeroPatternMobile />
  </>
);

export default RyanProductHeroPattern;
